/**
 * Platform Users screen (DNKADM-0015, DNKADM-0163, DNKADM-0167) — the admin-role
 * subset of the tenant's saas-auth login identities, backed by
 * `GET /v2/admin/platform-members` (bearer-only `/v2/admin/**`, tenant-from-JWT).
 * It answers "which admins can access this tenant" and shows each admin's
 * location access.
 *
 * This is a DISTINCT axis from the operational Users screen (which lists booking
 * contacts): the header makes that explicit and the two are NOT cross-linked.
 *
 * Reads are open to all admins; the WRITE surface (DNKADM-0167) is OWNER-only and
 * gated in the frontend on `canManageTeam` — `tenant_role === 'OWNER'`, or a human
 * platform support session (DNKADM-0483) — defense in depth, the server also returns
 * `403` for everyone else:
 *  - **Add platform user** — compose client-side: an email already in the roster is
 *    promoted (`setPlatformMemberRole`); a new email is invited
 *    (`invitePlatformMember`, then the Invited tab refreshes).
 *  - **Per-row role menu** + **disable/enable** — `setPlatformMember{Role,Status}`,
 *    each returning the canonical row dropped into the roster IN PLACE (via a local
 *    override map, not a refetch). The current OWNER's own row is guarded (controls
 *    disabled; the server also enforces `SELF_MODIFICATION_FORBIDDEN` /
 *    `LAST_OWNER_PROTECTED`, surfaced inline).
 *  - **Invited tab** — pending elevated invitations (`listPlatformInvitations`,
 *    filtered to the staff ladder) with per-row Cancel / Resend.
 *
 * DNKADM-0360 (dink-api handoff §3/§4): the roster says what each row IS via `kind`
 * (`STAFF`/`CUSTOMER`) instead of the client inferring it from `role`, so this screen
 * shows the STAFF rows and keys them on `userId` + `kind` — a person who is both a
 * customer and staff is two rows with one `userId`, never merged. The assignable role set
 * is the staff ladder `OWNER | ADMIN | STAFF`: `USER` is a customer state invited from the
 * Users screen, and SUPER_ADMIN/GUEST are rejected upstream (SUPER_ADMIN still RENDERS as
 * a badge for existing data). Write failures surface INLINE via
 * `toPlatformMemberWriteErrorView`, never as a full-screen error region.
 *
 * States (first-class, via `useResource`):
 *  - loading        → skeleton rows;
 *  - error + retry  → FAIL-CLOSED on any non-200 ("couldn't load … try again",
 *                     NEVER "0 members"); a `403` renders the not-authorized notice;
 *  - empty-but-loaded → a genuine empty `200` roster, distinct from the error state;
 *  - ready          → the table.
 *
 * DNKADM-0430 P1-F: both tabs use the shared {@link ListToolbar} and {@link SortableHeader},
 * and the tab, search and sort live in the URL (`platformMembersListView.ts`). The roster
 * keeps its Email-ascending default; blanks now sort last in both directions.
 */
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputLabel,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ApiError } from '@/api/errors';
import {
  cancelPlatformInvitation,
  getLocationAccess,
  getPlatformMembers,
  invitePlatformMember,
  listPlatformInvitations,
  resendPlatformInvitation,
  setLocationAccess,
  setPlatformMemberRole,
  setPlatformMemberStatus,
  type AssignableRole,
  type PlatformInvitation,
  type PlatformMember,
  type PlatformMemberLocationAccess,
  type SetPlatformMemberLocationAccessBody,
  type SettableStatus,
} from '@/api/platformMembers';
import { listLocations, type V2Location } from '@/api/locations';
import { locationScopedErrorMessage, serverMessage } from '@/api/serverMessage';
import { v2ErrorCode } from '@/api/v2Errors';
import {
  isStaffRosterRow,
  rosterDisplayRole,
  rosterRowKey,
  STAFF_ROSTER_ROLES,
} from '@/domain/rosterKind';
import { getClaims } from '@/auth/authStore';
import { deriveOperator } from '@/auth/authContextValue';
import type { JwtPayload } from '@/utils/jwt';
import { Icon, chipSx, inkAlpha, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { ownerOnlyInviteMessage, roleLimitedMessage } from '@/components/accessCopy';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { parseOption, useListUrlState } from '@/components/list/useListUrlState';
import { toPlatformMemberWriteErrorView } from './platformMembersError';
import { roleBadge, statusBadge } from './platformBadges';
import {
  INVITED_DEFAULT_SORT,
  INVITED_SORT_KEYS,
  MEMBER_DEFAULT_SORT,
  MEMBER_SORT_KEYS,
  PLATFORM_MEMBERS_URL_DEFAULTS,
  SCREEN_TABS,
  canEditLocationAccess,
  invitedMatches,
  invitedSortValue,
  memberMatches,
  memberSortValue,
  type InvitedSortKey,
  type MemberSortKey,
  type ScreenTab,
} from './platformMembersListView';
import { ScreenError } from '@/components/ScreenError';
import { useCalendarZone } from '@/location/useCalendarZone';
import { DATE_ONLY, formatInstantMs, instantMs } from '@/utils/instantFormat';

const SKELETON_ROWS = 6;
const PLACEHOLDER = '—';
/**
 * The roles an OWNER may assign, in menu order — the STAFF ladder (DNKADM-0360).
 * No SUPER_ADMIN (token-only now, display-only here) and no USER: a customer is a `kind`,
 * invited from the Users screen's "Invite user", not a role granted on this screen.
 */
const ASSIGNABLE_ROLES: readonly AssignableRole[] = ['OWNER', 'ADMIN', 'STAFF'];

type AccessMode = 'all' | 'specific';

function platformMembersTabA11yProps(tab: ScreenTab) {
  return {
    id: `platform-members-tab-${tab}`,
    'aria-controls': `platform-members-tabpanel-${tab}`,
  };
}

function platformMembersPanelA11yProps(tab: ScreenTab) {
  return {
    role: 'tabpanel' as const,
    id: `platform-members-tabpanel-${tab}`,
    'aria-labelledby': `platform-members-tab-${tab}`,
    tabIndex: 0,
  };
}

/** Inline banner shown after a member write (role/status/add). */
interface WriteFeedback {
  severity: 'success' | 'error';
  message: string;
}

/**
 * True when a roster row is the CURRENT operator's own login — used for the
 * self-modification guard on the role/disable controls. Matches on the JWT `email`
 * claim (case-insensitive) first, then a `sub` claim that equals the numeric
 * `userId`; either is sufficient, and an absent claim never matches.
 */
function isSelfMember(member: PlatformMember, claims: JwtPayload | null): boolean {
  if (!claims) return false;
  const claimEmail = typeof claims.email === 'string' ? claims.email.trim().toLowerCase() : '';
  if (claimEmail && member.email && member.email.trim().toLowerCase() === claimEmail) return true;
  const claimSub = typeof claims.sub === 'string' ? claims.sub.trim() : '';
  return claimSub.length > 0 && String(member.userId) === claimSub;
}

/**
 * Format an ISO instant as a short absolute date on the venue's clock (DNKADM-0476 F-05), or the
 * placeholder when absent/unparseable.
 */
function formatDate(iso: string | null, timeZone: string | null): string {
  const ms = instantMs(iso);
  return ms === null ? PLACEHOLDER : formatInstantMs(ms, timeZone, DATE_ONLY);
}

export function PlatformMembersScreen() {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const mode = theme.palette.mode;
  const roster = useResource(
    useCallback(() => getPlatformMembers(), []),
    [],
  );
  const locations = useResource(
    useCallback(() => listLocations({ status: 'active' }), []),
    [],
  );

  // DNKADM-0430: tab, search and sort come from the URL (`platformMembersListView.ts`).
  const url = useListUrlState(PLATFORM_MEMBERS_URL_DEFAULTS);
  const setUrl = url.set;
  const tab = parseOption<ScreenTab>(url.values.tab, SCREEN_TABS, 'members');
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, MEMBER_SORT_KEYS, MEMBER_DEFAULT_SORT);
  const [editing, setEditing] = useState<PlatformMember | null>(null);
  // DNKADM-0453: the row whose "change to Staff" is open in the location-choice dialog.
  const [staffTarget, setStaffTarget] = useState<PlatformMember | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // Canonical rows returned by role/status writes, dropped into the roster IN PLACE so a
  // write reflects immediately without a refetch. Keyed by `rosterRowKey` — `userId` alone
  // would apply one write to BOTH rows of a dual-role person (DNKADM-0360), duplicating
  // them in the table.
  const [overrides, setOverrides] = useState<Map<string, PlatformMember>>(new Map());
  const [feedback, setFeedback] = useState<WriteFeedback | null>(null);
  // Bumped after an invite so the (lazily mounted) Invited tab refetches.
  const [invitedRefreshKey, setInvitedRefreshKey] = useState(0);

  const onSort = useCallback(
    (key: MemberSortKey) => setUrl(toSortParams(toggleSort(sort, key), MEMBER_DEFAULT_SORT)),
    [setUrl, sort],
  );
  // A tab change clears the search and sort: each tab has its own columns.
  const onTabChange = useCallback(
    (next: ScreenTab) => setUrl({ tab: next, q: null, sort: null, dir: null }),
    [setUrl],
  );

  const loaded = roster.data?.members;
  const claims = getClaims();
  // OWNER, or a human platform support session (DNKADM-0483) — derived once in
  // `deriveOperator`, never re-read from the claims here.
  const canManageTeam = deriveOperator(claims)?.canManageTeam ?? false;

  // The roster with any write-returned canonical rows applied in place.
  const mergedMembers = useMemo(() => {
    const base = loaded ?? [];
    if (overrides.size === 0) return base;
    return base.map((m) => overrides.get(rosterRowKey(m)) ?? m);
  }, [loaded, overrides]);

  // The staff rows — `kind` when the row carries it, else the role set (DNKADM-0360).
  // This is a DISPLAY filter for a staff-and-access screen, never an authorization gate.
  const staffMembers = useMemo(
    () => mergedMembers.filter((m) => isStaffRosterRow(m)),
    [mergedMembers],
  );

  const locationNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const loc of locations.data ?? []) {
      map.set(loc.id, loc.name || loc.id);
    }
    return map;
  }, [locations.data]);

  // userId → display name, for the Invited tab's "invited by" join.
  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of mergedMembers) {
      map.set(String(m.userId), m.name || m.email || String(m.userId));
    }
    return map;
  }, [mergedMembers]);

  // Active owners named in the Invited tab's locked notice (DNKADM-0433), from the roster
  // already loaded - no extra call. Deduped by login (a dual-role person has two rows).
  const ownerNames = useMemo(() => {
    const seen = new Set<number>();
    const names: string[] = [];
    for (const m of mergedMembers) {
      if (m.role !== 'OWNER' || m.status !== 'ACTIVE' || seen.has(m.userId)) continue;
      seen.add(m.userId);
      const label = m.name || m.email;
      if (label) names.push(label);
    }
    return names;
  }, [mergedMembers]);

  // Client-side search and sort (default Email ascending; blanks last both ways), never
  // depending on any wire ordering. Ties fall back to the row identity.
  const rows = useMemo(
    () =>
      sortRows(
        staffMembers.filter((m) => memberMatches(m, search)),
        (m) => memberSortValue(m, sort.key),
        sort.dir,
        (m) => rosterRowKey(m),
      ),
    [staffMembers, search, sort.key, sort.dir],
  );

  const onClearSearch = useCallback(() => setUrl({ q: null }), [setUrl]);

  /**
   * Drop a write's canonical row into the roster in place of the row it was applied to.
   * The override is keyed by the TARGET row's identity (`rosterRowKey`), not by `userId`:
   * the same person can hold a customer row too, and only the row the operator acted on
   * may be replaced.
   */
  const applyMemberRow = useCallback((target: PlatformMember, row: PlatformMember) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(rosterRowKey(target), row);
      return next;
    });
  }, []);

  const handleRoleChange = useCallback(
    async (member: PlatformMember, role: AssignableRole) => {
      setFeedback(null);
      try {
        const updated = await setPlatformMemberRole(member.userId, role);
        applyMemberRow(member, updated);
        setFeedback({
          severity: 'success',
          message: `${member.email || 'Platform user'} is now ${roleBadge(role, mode).label}.`,
        });
      } catch (err) {
        setFeedback({ severity: 'error', message: toPlatformMemberWriteErrorView(err).message });
      }
    },
    [applyMemberRow, mode],
  );

  const handleStatusChange = useCallback(
    async (member: PlatformMember, status: SettableStatus) => {
      setFeedback(null);
      try {
        const updated = await setPlatformMemberStatus(member.userId, status);
        applyMemberRow(member, updated);
        setFeedback({
          severity: 'success',
          message:
            status === 'DISABLED'
              ? `${member.email || 'Platform user'} was disabled — all access removed.`
              : `${member.email || 'Platform user'} was re-enabled — prior access restored.`,
        });
      } catch (err) {
        setFeedback({ severity: 'error', message: toPlatformMemberWriteErrorView(err).message });
      }
    },
    [applyMemberRow],
  );

  // DNKADM-0453: changing to STAFF opens the location-choice dialog; the other roles change at once.
  const handleRoleMenu = useCallback(
    (member: PlatformMember, role: AssignableRole) => {
      if (role === 'STAFF') {
        setFeedback(null);
        setStaffTarget(member);
      }
      else void handleRoleChange(member, role);
    },
    [handleRoleChange],
  );

  /**
   * DNKADM-0453: the outcome of a role change that also saved location access (role first, then
   * the PUT). The row takes the server's row either way. When the role changed but the locations
   * did not save, the banner says so plainly and the row stays editable (Edit) - it never reads as
   * if All had been saved.
   */
  const handleRoleWithAccessDone = useCallback(
    (target: PlatformMember, result: RoleWithAccessResult) => {
      applyMemberRow(target, result.row);
      setAddOpen(false);
      setStaffTarget(null);
      const who = result.row.email || target.email || 'Platform user';
      const label = roleBadge(rosterDisplayRole(result.row), mode).label;
      if (result.accessError) {
        setFeedback({
          severity: 'error',
          message:
            `${who} is now ${label}, but their location access did not save: ${result.accessError} ` +
            `${rosterDisplayRole(result.row) === 'STAFF' ? 'Staff with no saved locations see nothing. ' : ''}` +
            'Use Edit on their row to set it.',
        });
      } else {
        setFeedback({ severity: 'success', message: `${who} is now ${label}.` });
      }
    },
    [applyMemberRow, mode],
  );

  const total = roster.status === 'ready' ? staffMembers.length : null;
  const countLabel =
    total !== null ? `${total} platform user${total === 1 ? '' : 's'}` : 'Platform users';

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 0.5,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h1" component="h1">
            Platform Users
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 640 }}>
            Staff with a <strong>login</strong> to this tenant and the locations they can access.
            This is separate from the operational Users screen, which lists booking contacts.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          {roster.status === 'ready' ? (
            <Typography variant="body2" noWrap>
              {countLabel}
            </Typography>
          ) : null}
          {canManageTeam ? (
            <Button
              variant="contained"
              startIcon={<Icon name="person_add" />}
              onClick={() => setAddOpen(true)}
              disabled={roster.status !== 'ready'}
            >
              Add platform user
            </Button>
          ) : (
            // DNKADM-0433: disabled with the reason, not hidden to 403 after a click. The span
            // lets the tooltip show on a disabled button.
            <Tooltip title={ownerOnlyInviteMessage(ownerNames)}>
              <span>
                <Button variant="contained" startIcon={<Icon name="person_add" />} disabled>
                  Add platform user
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Tabs
        value={tab}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        onChange={(_, v: ScreenTab) => onTabChange(v)}
        aria-label="Platform user sections"
        sx={{ borderBottom: `1px solid ${t.line}`, mt: 1.5 }}
      >
        <Tab value="members" label="Members" {...platformMembersTabA11yProps('members')} />
        <Tab value="invited" label="Invited" {...platformMembersTabA11yProps('invited')} />
      </Tabs>

      {feedback ? (
        <Alert
          severity={feedback.severity}
          onClose={() => setFeedback(null)}
          sx={{ mt: 2 }}
          role={feedback.severity === 'error' ? 'alert' : 'status'}
        >
          {feedback.message}
        </Alert>
      ) : null}

      {tab === 'members' ? (
        <Box {...platformMembersPanelA11yProps('members')}>
          <ListToolbar
            search={{
              value: search,
              onChange: (next) => setUrl({ q: next }),
              placeholder: 'Filter by name or email',
              label: 'Filter platform users',
              disabled: roster.status !== 'ready',
            }}
            onClear={onClearSearch}
          />
          {roster.status === 'error' ? (
            <ScreenError
              variant="panel"
              error={roster.error}
              what="the Platform Users roster"
              onRetry={roster.refetch}
              forbiddenMessage={roleLimitedMessage(
                'The Platform Users roster',
                'OWNER, SUPER_ADMIN, or ADMIN',
              )}
              title="Couldn’t load Platform Users"
              // DNKADM-0015 fail-closed: a 404 is still "roster unknown", never "removed".
              notFound={
                <ScreenError
                  variant="panel"
                  error={null}
                  what="the Platform Users roster"
                  onRetry={roster.refetch}
                  title="Couldn’t load Platform Users"
                  retryMessage={PLATFORM_ROSTER_UNAVAILABLE}
                  retryLabel="Try again"
                  sx={{ mt: 1 }}
                />
              }
              retryMessage={PLATFORM_ROSTER_UNAVAILABLE}
              rejectedMessage={PLATFORM_ROSTER_UNAVAILABLE}
              retryLabel="Try again"
              sx={{ mt: 1 }}
            />
          ) : (
            <Paper elevation={1} sx={{ overflow: 'hidden' }}>
              <TableContainer>
                <Table aria-label="Platform users">
                  <TableHead>
                    <TableRow>
                      <SortableHeader
                        label="Name"
                        col="name"
                        sort={sort}
                        onSort={onSort}
                        disabled={roster.status !== 'ready'}
                      />
                      <SortableHeader
                        label="Email"
                        col="email"
                        sort={sort}
                        onSort={onSort}
                        disabled={roster.status !== 'ready'}
                      />
                      <SortableHeader
                        label="Role"
                        col="role"
                        sort={sort}
                        onSort={onSort}
                        disabled={roster.status !== 'ready'}
                      />
                      <SortableHeader
                        label="Status"
                        col="status"
                        sort={sort}
                        onSort={onSort}
                        disabled={roster.status !== 'ready'}
                      />
                      <TableCell>Location access</TableCell>
                      {canManageTeam ? <TableCell align="right">Actions</TableCell> : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roster.status === 'loading'
                      ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                          <SkeletonRow key={`sk-${i}`} showActions={canManageTeam} />
                        ))
                      : rows.map((m) => (
                          <PlatformMemberRow
                            // `userId` alone is NOT unique since DNKAPI-0517 — a person who
                            // is both a customer and staff is two rows sharing it
                            // (DNKADM-0360). Key on the pair, never merge the rows.
                            key={rosterRowKey(m)}
                            member={m}
                            locationNames={locationNames}
                            locationsReady={locations.status === 'ready'}
                            canEdit={canManageTeam && canEditLocationAccess(m)}
                            onEdit={setEditing}
                            showActions={canManageTeam}
                            isSelf={isSelfMember(m, claims)}
                            onRoleChange={handleRoleMenu}
                            onStatusChange={handleStatusChange}
                          />
                        ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {roster.status === 'ready' && rows.length === 0 && (
                <EmptyState
                  search={search}
                  hasMembers={staffMembers.length > 0}
                  onClearSearch={onClearSearch}
                />
              )}
            </Paper>
          )}
        </Box>
      ) : (
        <Box {...platformMembersPanelA11yProps('invited')}>
          <InvitedPanel
            canManageTeam={canManageTeam}
            ownerNames={ownerNames}
            memberNames={memberNames}
            locationNames={locationNames}
            refreshKey={invitedRefreshKey}
          />
        </Box>
      )}

      {editing ? (
        <LocationAccessDialog
          member={editing}
          locations={locations.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            applyMemberRow(editing, updated);
            setEditing(null);
          }}
        />
      ) : null}

      {staffTarget ? (
        <StaffRoleDialog
          member={staffTarget}
          locations={locations.data ?? []}
          onClose={() => setStaffTarget(null)}
          onDone={handleRoleWithAccessDone}
        />
      ) : null}

      {addOpen ? (
        <AddPlatformUserDialog
          members={mergedMembers}
          locations={locations.data ?? []}
          onClose={() => setAddOpen(false)}
          onPromoted={handleRoleWithAccessDone}
          onInvited={(invitation) => {
            setAddOpen(false);
            setInvitedRefreshKey((k) => k + 1);
            setFeedback({
              severity: 'success',
              message: invitation.email
                ? `Invitation sent to ${invitation.email}.`
                : 'Invitation sent.',
            });
          }}
        />
      ) : null}
    </Box>
  );
}

function SkeletonRow({ showActions }: { showActions: boolean }) {
  return (
    <TableRow>
      <TableCell>
        <Skeleton width={140} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={180} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={66} height={20} />
      </TableCell>
      <TableCell>
        <Skeleton width={72} height={20} />
      </TableCell>
      <TableCell>
        <Skeleton width={132} height={18} />
      </TableCell>
      {showActions ? (
        <TableCell align="right">
          <Skeleton width={54} height={32} sx={{ ml: 'auto' }} />
        </TableCell>
      ) : null}
    </TableRow>
  );
}

function PlatformMemberRow({
  member,
  locationNames,
  locationsReady,
  canEdit,
  onEdit,
  showActions,
  isSelf,
  onRoleChange,
  onStatusChange,
}: {
  member: PlatformMember;
  locationNames: Map<string, string>;
  locationsReady: boolean;
  canEdit: boolean;
  onEdit: (member: PlatformMember) => void;
  showActions: boolean;
  isSelf: boolean;
  onRoleChange: (member: PlatformMember, role: AssignableRole) => void;
  onStatusChange: (member: PlatformMember, status: SettableStatus) => void;
}) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  // The displayed role comes from what the row SAYS it is (DNKADM-0360): the wire role
  // when there is one, else the customer `state` / the STAFF floor role. A blank role is
  // "unknown" and renders the placeholder — it is never read as 'USER'.
  const displayedRole = rosterDisplayRole(member);
  const role = roleBadge(displayedRole, mode);
  const status = statusBadge(member.status, mode);
  const locationLabel = locationAccessLabel(member, locationNames, locationsReady);
  const noUsableLocation = locationLabel === STAFF_NO_LOCATIONS_LABEL;
  return (
    <TableRow hover>
      <TableCell>
        <Typography sx={{ fontWeight: 500 }}>{member.name || PLACEHOLDER}</Typography>
      </TableCell>
      <TableCell sx={{ color: inkAlpha(theme, 0.7) }}>{member.email || PLACEHOLDER}</TableCell>
      <TableCell>{displayedRole ? <BadgeChip badge={role} /> : PLACEHOLDER}</TableCell>
      <TableCell>{member.status ? <BadgeChip badge={status} /> : PLACEHOLDER}</TableCell>
      <TableCell>
        <Typography variant="body2" sx={noUsableLocation ? { fontWeight: 600 } : undefined}>
          {locationLabel}
        </Typography>
      </TableCell>
      {showActions ? (
        <TableCell align="right">
          <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
            {canEdit ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Icon name="edit" />}
                onClick={() => onEdit(member)}
              >
                Edit
              </Button>
            ) : null}
            <RowActionsMenu
              member={member}
              isSelf={isSelf}
              onRoleChange={onRoleChange}
              onStatusChange={onStatusChange}
            />
          </Stack>
        </TableCell>
      ) : null}
    </TableRow>
  );
}

/**
 * Per-row overflow menu (OWNER only) with the role choices (Owner/Admin/User) and
 * the disable/enable toggle. The whole control is DISABLED on the operator's own
 * row (the self-modification guard); the server enforces it again with
 * `SELF_MODIFICATION_FORBIDDEN` / `LAST_OWNER_PROTECTED`, surfaced inline by the caller.
 */
function RowActionsMenu({
  member,
  isSelf,
  onRoleChange,
  onStatusChange,
}: {
  member: PlatformMember;
  isSelf: boolean;
  onRoleChange: (member: PlatformMember, role: AssignableRole) => void;
  onStatusChange: (member: PlatformMember, status: SettableStatus) => void;
}) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const name = member.name || member.email || String(member.userId);
  const isDisabled = member.status === 'DISABLED';

  return (
    <>
      <Tooltip title={isSelf ? 'You can’t change your own role or status' : 'Manage access'}>
        <span>
          <IconButton
            size="small"
            aria-label={`Manage ${name}`}
            aria-haspopup="menu"
            disabled={isSelf}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <Icon name="more_vert" sx={{ fontSize: 20 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <ListSubheader disableSticky sx={{ lineHeight: 2, bgcolor: 'transparent' }}>
          Change role
        </ListSubheader>
        {ASSIGNABLE_ROLES.map((r) => (
          <MenuItem
            key={r}
            selected={member.role === r}
            disabled={member.role === r}
            onClick={() => {
              setAnchorEl(null);
              onRoleChange(member, r);
            }}
          >
            {roleBadge(r, mode).label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onStatusChange(member, isDisabled ? 'ACTIVE' : 'DISABLED');
          }}
        >
          <ListItemText
            primary={isDisabled ? 'Enable access' : 'Disable access'}
            secondary={
              isDisabled
                ? 'Restores their prior location access.'
                : 'Removes all access. Re-enabling restores their prior locations.'
            }
            slotProps={{ secondary: { sx: { maxWidth: 240, whiteSpace: 'normal' } } }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}

/** DNKADM-0451: the copy for a STAFF login whose access resolves to no usable location. */
const STAFF_NO_LOCATIONS_LABEL = 'No locations: this login sees nothing';

/**
 * Does this STAFF row have no location it can actually use? True when it has no ids at all
 * (DNKAPI-0519/0659: a STAFF with nothing stored reads NONE), or — once the active-location
 * list has loaded — when none of its ids is an active location (all archived or unknown).
 * The second check waits for `locationsReady`: before the list loads every id looks
 * unknown, and the row must not flash a false warning. Display only; the server enforces.
 */
function staffHasNoUsableLocation(
  member: PlatformMember,
  locationNames: Map<string, string>,
  locationsReady: boolean,
): boolean {
  if (!isStaffRosterRow(member) || rosterDisplayRole(member) !== 'STAFF') return false;
  if (member.locationAccess.allLocations) return false;
  const ids = member.locationAccess.locationIds;
  if (ids.length === 0) return true;
  return locationsReady && ids.every((id) => !locationNames.has(id));
}

function locationAccessLabel(
  member: PlatformMember,
  locationNames: Map<string, string>,
  locationsReady: boolean,
): string {
  if (member.role === 'OWNER' || member.locationAccess.allLocations) return 'All locations';
  if (staffHasNoUsableLocation(member, locationNames, locationsReady)) {
    return STAFF_NO_LOCATIONS_LABEL;
  }
  if (member.locationAccess.locationIds.length === 0) return PLACEHOLDER;
  return member.locationAccess.locationIds.map((id) => locationNames.get(id) ?? id).join(', ');
}

function BadgeChip({ badge }: { badge: ReturnType<typeof roleBadge> }) {
  const chip = <Chip size="small" label={badge.label} sx={chipSx(badge)} />;
  if (badge.hint) {
    return <Tooltip title={badge.hint}>{chip}</Tooltip>;
  }
  return chip;
}

function EmptyState({
  search,
  hasMembers,
  onClearSearch,
}: {
  search: string;
  hasMembers: boolean;
  onClearSearch: () => void;
}) {
  const theme = useTheme();
  // A non-empty filter that matched nothing → the filter-empty state.
  if (search.trim().length > 0 && hasMembers) {
    return (
      <Box sx={{ p: 7, textAlign: 'center' }}>
        <Icon name="person_search" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
        <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
          No platform users match “{search}”
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 360, mx: 'auto' }}>
          Check the spelling, or try part of an email.
        </Typography>
        <Button variant="outlined" color="primary" sx={{ mt: 2 }} onClick={onClearSearch}>
          Clear filter
        </Button>
      </Box>
    );
  }

  // A genuine empty-but-loaded roster (the server returned zero logins). This is a
  // NORMAL state, deliberately distinct from the fail-closed error state.
  return (
    <Box sx={{ p: 7, textAlign: 'center' }}>
      <Icon name="badge" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>No platform users yet</Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 360, mx: 'auto' }}>
        No owner, admin, or staff login was returned for this tenant.
      </Typography>
    </Box>
  );
}

/**
 * "Add platform user" (OWNER only). Composes client-side: an email already in the
 * roster we hold is PROMOTED (`setPlatformMemberRole`); a new email is INVITED
 * (`invitePlatformMember` — the server sends the email; `inviteUrl` is ignored). Business
 * errors surface inline via the shared write-error classifier.
 *
 * DNKADM-0453: STAFF and ADMIN carry a required "Location access" choice (default All
 * locations). An invite sends it as `locationAccess` (held by the server until the invitee
 * joins); a role change on an existing CUSTOMER login sends the role, then the location-access
 * PUT. OWNER sends nothing ("Owners always have all locations"). An existing STAFF-ladder login
 * keeps the access it has: changing its role here never overwrites its locations.
 */
function AddPlatformUserDialog({
  members,
  locations,
  onClose,
  onPromoted,
  onInvited,
}: {
  members: PlatformMember[];
  locations: V2Location[];
  onClose: () => void;
  /** `(the roster row the write targeted, the outcome of the role + access writes)`. */
  onPromoted: (target: PlatformMember, result: RoleWithAccessResult) => void;
  onInvited: (invitation: PlatformInvitation) => void;
}) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const ids = useId();
  const titleId = `${ids}-title`;
  const roleLabelId = `${ids}-role-label`;
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AssignableRole>('ADMIN');
  const [name, setName] = useState('');
  const [accessMode, setAccessMode] = useState<AccessMode>('all');
  const [accessIds, setAccessIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const trimmedEmail = email.trim();
  // One email can match TWO rows since DNKAPI-0517 (a person who is both a customer and
  // staff). This dialog grants a STAFF-ladder role, so it targets the STAFF row when
  // there is one — that is the row the write replaces — and falls back to whatever
  // matched when the person is only a customer today (a promotion).
  const existing = useMemo(() => {
    if (trimmedEmail.length === 0) return undefined;
    const matches = members.filter(
      (m) => m.email.trim().toLowerCase() === trimmedEmail.toLowerCase(),
    );
    return matches.find((m) => isStaffRosterRow(m)) ?? matches[0];
  }, [members, trimmedEmail]);

  // A demotion to STAFF (an Admin/Owner becoming Staff) asks for the choice too: a STAFF with
  // nothing saved has NO locations (DNKAPI-0659). Any other change on a staff login keeps its access.
  const existingIsStaff = existing ? isStaffRosterRow(existing) : false;
  const demotingToStaff =
    existing !== undefined &&
    existingIsStaff &&
    role === 'STAFF' &&
    rosterDisplayRole(existing) !== 'STAFF';
  const showChoice = role !== 'OWNER' && (!existingIsStaff || demotingToStaff);

  // Re-seed the choice when the matched login or the role changes (state adjusted during render).
  const seedKey = `${existing ? rosterRowKey(existing) : ''}|${role}`;
  const [seededFor, setSeededFor] = useState(seedKey);
  if (seededFor !== seedKey) {
    setSeededFor(seedKey);
    const seed = initialStaffChoice(demotingToStaff ? existing : undefined);
    setAccessMode(seed.mode);
    setAccessIds(seed.ids);
  }
  const pickMissing = showChoice && accessMode === 'specific' && accessIds.length === 0;

  async function handleSubmit() {
    setInlineError(null);
    if (trimmedEmail.length === 0) {
      setInlineError('Enter an email address.');
      return;
    }
    if (pickMissing) {
      setInlineError(PICK_LOCATION_MESSAGE);
      return;
    }
    const access: SetPlatformMemberLocationAccessBody | undefined = showChoice
      ? toAccessBody(accessMode, accessIds)
      : undefined;
    setSubmitting(true);
    try {
      if (existing) {
        onPromoted(existing, await changeRoleThenAccess(existing.userId, role, access));
      } else {
        const trimmedName = name.trim();
        const invitation = await invitePlatformMember({
          email: trimmedEmail,
          role,
          ...(trimmedName.length > 0 ? { name: trimmedName } : {}),
          ...(access ? { locationAccess: access } : {}),
        });
        onInvited(invitation);
      }
    } catch (err) {
      setInlineError(toPlatformMemberWriteErrorView(err).message);
    } finally {
      setSubmitting(false);
    }
  }

  const submitLabel = existing ? 'Update role' : 'Send invite';

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={titleId}>Add platform user</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Enter an email. If they already have a login, their role is updated; otherwise we send
            an invitation.
          </Typography>
          <TextField
            label="Email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            fullWidth
            slotProps={{ htmlInput: { 'aria-label': 'Email' } }}
          />
          <FormControl fullWidth disabled={submitting}>
            <InputLabel id={roleLabelId}>Role</InputLabel>
            <Select
              labelId={roleLabelId}
              value={role}
              label="Role"
              onChange={(e) => setRole(e.target.value as AssignableRole)}
            >
              {ASSIGNABLE_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {roleBadge(r, mode).label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {role === 'OWNER' ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {OWNER_ALL_LOCATIONS_LINE}
            </Typography>
          ) : showChoice ? (
            <LocationAccessChoice
              mode={accessMode}
              onModeChange={setAccessMode}
              selectedIds={accessIds}
              onSelectedChange={setAccessIds}
              locations={locations}
              disabled={submitting}
              invalid={inlineError === PICK_LOCATION_MESSAGE}
            />
          ) : null}
          <TextField
            label="Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            fullWidth
            slotProps={{ htmlInput: { 'aria-label': 'Name' } }}
          />
          {existing ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {existing.email} already has a login — this will change their role.
            </Typography>
          ) : null}
          {inlineError ? (
            <Alert severity="error" role="alert">
              {inlineError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The Invited tab — pending elevated invitations from `listPlatformInvitations`,
 * filtered client-side to the staff ladder (DNKADM-0360; USER/GUEST invites belong to
 * the Users screen, DNKADM-0168). The tab is READABLE by all admins; the per-row Cancel /
 * Resend actions are OWNER-only. Cancel removes the row; Resend replaces it with the
 * fresh invitation (rotated token / reset expiry) returned by the endpoint. The
 * removed/replaced overlays keep those user edits without an effect-driven copy of
 * the fetched list.
 */
function InvitedPanel({
  canManageTeam,
  ownerNames,
  memberNames,
  locationNames,
  refreshKey,
}: {
  canManageTeam: boolean;
  ownerNames: string[];
  memberNames: Map<string, string>;
  locationNames: Map<string, string>;
  refreshKey: number;
}) {
  const calendarZone = useCalendarZone();
  const theme = useTheme();
  const mode = theme.palette.mode;
  // DNKADM-0430: search and sort come from the URL (`platformMembersListView.ts`).
  const url = useListUrlState(PLATFORM_MEMBERS_URL_DEFAULTS);
  const setUrl = url.set;
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, INVITED_SORT_KEYS, INVITED_DEFAULT_SORT);
  const onSort = useCallback(
    (key: InvitedSortKey) => setUrl(toSortParams(toggleSort(sort, key), INVITED_DEFAULT_SORT)),
    [setUrl, sort],
  );
  const invites = useResource(
    useCallback(() => listPlatformInvitations('PENDING'), []),
    [refreshKey],
  );
  // Row-local edits layered over the fetched list: cancelled ids drop out;
  // resent ids render their fresh invitation. Keyed by the ORIGINAL invitation id
  // so a resend (which may rotate the id) still matches its row.
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [replaced, setReplaced] = useState<Map<string, PlatformInvitation>>(new Map());
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  /** The "Invited by" text: a roster name, else the raw inviter id, else `null`. */
  const invitedByOf = useCallback(
    (inv: PlatformInvitation): string | null =>
      (inv.invitedByUserId && memberNames.get(inv.invitedByUserId)) || inv.invitedByUserId,
    [memberNames],
  );

  const visible = useMemo(() => {
    return (
      (invites.data ?? [])
        // DNKADM-0360: show the STAFF-ladder invitations (STAFF/ADMIN/OWNER, plus an
        // existing SUPER_ADMIN one so it stays cancellable — see `STAFF_ROSTER_ROLES`). A
        // USER/GUEST invitation belongs to the Users screen and is never listed here;
        // an unknown future role is likewise not assumed to be elevated.
        .filter((inv) => STAFF_ROSTER_ROLES.has(inv.role))
        .filter((inv) => !removed.has(inv.invitationId))
        .map((inv) => {
          const fresh = replaced.get(inv.invitationId);
          // A resend answers a fresh invitation; keep the pending access if it omits the field.
          const shown =
            fresh && fresh.locationAccess === undefined && inv.locationAccess !== undefined
              ? { ...fresh, locationAccess: inv.locationAccess }
              : (fresh ?? inv);
          return { key: inv.invitationId, inv: shown };
        })
    );
  }, [invites.data, removed, replaced]);

  // DNKADM-0430: the search, then the column sort (default Invited, newest first).
  const rows = useMemo(
    () =>
      sortRows(
        visible.filter(({ inv }) => invitedMatches(inv, search)),
        ({ inv }) => invitedSortValue(inv, sort.key, invitedByOf(inv)),
        sort.dir,
        ({ key }) => key,
      ),
    [visible, search, sort.key, sort.dir, invitedByOf],
  );

  async function handleCancel(key: string, invitationId: string) {
    setInlineError(null);
    setBusyId(key);
    try {
      await cancelPlatformInvitation(invitationId);
      setRemoved((prev) => new Set(prev).add(key));
    } catch (err) {
      const view = toPlatformMemberWriteErrorView(err);
      setInlineError(view.message);
      if (view.refreshList) invites.refetch();
    } finally {
      setBusyId(null);
    }
  }

  async function handleResend(key: string, invitationId: string) {
    setInlineError(null);
    setBusyId(key);
    try {
      const fresh = await resendPlatformInvitation(invitationId);
      setReplaced((prev) => new Map(prev).set(key, fresh));
    } catch (err) {
      const view = toPlatformMemberWriteErrorView(err);
      setInlineError(view.message);
      if (view.refreshList) invites.refetch();
    } finally {
      setBusyId(null);
    }
  }

  // DNKADM-0433/0440: a 403 is "not allowed", never a network failure - say why and who to
  // ask (a non-owner is told which owners can invite), with no Try again.
  if (invites.status === 'error') {
    return (
      <ScreenError
        variant="panel"
        error={invites.error}
        what="invitations"
        onRetry={invites.refetch}
        forbiddenMessage={canManageTeam ? undefined : ownerOnlyInviteMessage(ownerNames)}
        retryMessage="We couldn’t reach the pending invitations. Please try again."
        retryLabel="Try again"
        sx={{ mt: 2 }}
      />
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {inlineError ? (
        <Alert severity="error" role="alert" onClose={() => setInlineError(null)} sx={{ mb: 2 }}>
          {inlineError}
        </Alert>
      ) : null}
      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search invitations',
          disabled: invites.status !== 'ready',
        }}
        onClear={() => setUrl({ q: null })}
      />
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table aria-label="Pending invitations">
            <TableHead>
              <TableRow>
                <SortableHeader label="Email" col="email" sort={sort} onSort={onSort} />
                <SortableHeader label="Role" col="role" sort={sort} onSort={onSort} />
                <SortableHeader label="Invited by" col="invitedBy" sort={sort} onSort={onSort} />
                <SortableHeader label="Invited" col="invited" sort={sort} onSort={onSort} />
                <SortableHeader label="Expires" col="expires" sort={sort} onSort={onSort} />
                {canManageTeam ? <TableCell align="right">Actions</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.status === 'loading'
                ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                    <InvitedSkeletonRow key={`sk-${i}`} showActions={canManageTeam} />
                  ))
                : rows.map(({ key, inv }) => {
                    const invitedBy = invitedByOf(inv) || PLACEHOLDER;
                    const role = roleBadge(inv.role, mode);
                    const pendingAccess = pendingAccessLabel(inv.locationAccess, locationNames);
                    return (
                      <TableRow key={key} hover>
                        <TableCell sx={{ color: inkAlpha(theme, 0.7) }}>
                          {inv.email || PLACEHOLDER}
                        </TableCell>
                        <TableCell>
                          {inv.role ? <BadgeChip badge={role} /> : PLACEHOLDER}
                          {pendingAccess ? (
                            // DNKADM-0453: the access chosen at invite, applied when they join.
                            <Typography
                              variant="caption"
                              component="div"
                              sx={{ color: 'text.secondary', mt: 0.5 }}
                            >
                              {pendingAccess}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{invitedBy}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(inv.createdAt, calendarZone)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(inv.expiresAt, calendarZone)}</Typography>
                        </TableCell>
                        {canManageTeam ? (
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={busyId === key}
                                onClick={() => void handleResend(key, inv.invitationId)}
                              >
                                Resend
                              </Button>
                              <Button
                                size="small"
                                color="inherit"
                                disabled={busyId === key}
                                onClick={() => void handleCancel(key, inv.invitationId)}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
        {invites.status === 'ready' && rows.length === 0 && visible.length > 0 ? (
          <ListNoMatch noun="invitations" query={search} />
        ) : null}
        {invites.status === 'ready' && visible.length === 0 ? (
          <Box sx={{ p: 7, textAlign: 'center' }}>
            <Icon name="mail" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
            <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
              No pending invitations
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 360, mx: 'auto' }}>
              Elevated invitations you send will appear here until they’re accepted.
            </Typography>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
}

function InvitedSkeletonRow({ showActions }: { showActions: boolean }) {
  return (
    <TableRow>
      <TableCell>
        <Skeleton width={180} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={66} height={20} />
      </TableCell>
      <TableCell>
        <Skeleton width={120} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={90} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={90} height={12} />
      </TableCell>
      {showActions ? (
        <TableCell align="right">
          <Skeleton width={120} height={32} sx={{ ml: 'auto' }} />
        </TableCell>
      ) : null}
    </TableRow>
  );
}

function LocationAccessDialog({
  member,
  locations,
  onClose,
  onSaved,
}: {
  member: PlatformMember;
  locations: V2Location[];
  onClose: () => void;
  onSaved: (updated: PlatformMember) => void;
}) {
  const ids = useId();
  const titleId = `${ids}-title`;
  const locationsLabelId = `${ids}-locations-label`;
  const [mode, setMode] = useState<AccessMode>(
    member.locationAccess.allLocations ? 'all' : 'specific',
  );
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>(
    member.locationAccess.locationIds,
  );
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMode(member.locationAccess.allLocations ? 'all' : 'specific');
    setSelectedLocationIds(member.locationAccess.locationIds);
    setInlineError(null);
    setLoadingAccess(true);
    void (async () => {
      try {
        const access = await getLocationAccess(member.userId);
        if (cancelled) return;
        seedAccess(access, setMode, setSelectedLocationIds);
      } catch (err) {
        if (cancelled) return;
        setInlineError(classifyLocationAccessError(err));
      } finally {
        if (!cancelled) setLoadingAccess(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [member]);

  async function handleSave() {
    setInlineError(null);
    if (mode === 'specific' && selectedLocationIds.length === 0) {
      setInlineError('Pick at least one location, or choose All locations.');
      return;
    }
    setSaving(true);
    try {
      const saved = await saveLocationAccess(
        member.userId,
        toAccessBody(mode, selectedLocationIds),
      );
      onSaved({ ...member, locationAccess: saved });
    } catch (err) {
      setInlineError(classifyLocationAccessError(err));
    } finally {
      setSaving(false);
    }
  }

  const selectedNames = (selected: string[]) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((id) => {
        const loc = locations.find((l) => l.id === id);
        return <Chip key={id} size="small" label={loc ? loc.name || loc.id : id} />;
      })}
    </Box>
  );

  const busy = saving || loadingAccess;

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={titleId}>Edit location access</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 600 }}>
              {member.name || member.email || member.userId}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {member.email || PLACEHOLDER}
            </Typography>
          </Box>
          {rosterDisplayRole(member) === 'STAFF' ? (
            // DNKADM-0360 / handoff §2: a new STAFF has NO location assignment and sees
            // nothing location-scoped until an owner grants one — the empty state on the
            // roster row is expected, not a data fault.
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Staff start with no locations and see nothing location-scoped until you assign one
              here.
            </Typography>
          ) : null}
          <FormControl component="fieldset" disabled={saving}>
            <RadioGroup
              value={mode}
              onChange={(e) => setMode(e.target.value as AccessMode)}
              name="location-access-mode"
            >
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                label="All locations"
              />
              <FormControlLabel
                value="specific"
                control={<Radio size="small" />}
                label="Specific locations"
              />
            </RadioGroup>
          </FormControl>

          {mode === 'specific' ? (
            <FormControl
              size="small"
              fullWidth
              disabled={saving || loadingAccess || locations.length === 0}
              error={inlineError === 'Pick at least one location, or choose All locations.'}
            >
              <InputLabel id={locationsLabelId}>Specific locations</InputLabel>
              <Select
                labelId={locationsLabelId}
                multiple
                value={selectedLocationIds}
                onChange={(e) =>
                  setSelectedLocationIds(
                    typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                  )
                }
                input={<OutlinedInput label="Specific locations" />}
                renderValue={selectedNames}
              >
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    <Checkbox checked={selectedLocationIds.includes(loc.id)} size="small" />
                    <ListItemText primary={loc.name || loc.id} />
                  </MenuItem>
                ))}
              </Select>
              {locations.length === 0 ? (
                <FormHelperText>No locations are available to assign.</FormHelperText>
              ) : null}
            </FormControl>
          ) : null}

          {loadingAccess ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Loading current access…
            </Typography>
          ) : null}
          {inlineError ? (
            <Alert severity="error" role="alert">
              {inlineError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={busy}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const PICK_LOCATION_MESSAGE = 'Pick at least one location, or choose All locations.';
/** DNKADM-0453: shown in place of the choice when the role is Owner (nothing is sent). */
const OWNER_ALL_LOCATIONS_LINE = 'Owners always have all locations';

/**
 * DNKADM-0453: where the "change to Staff" choice starts - the login's current specific list when
 * it has one (so a click does not widen it), else All locations. Shared by both dialogs.
 */
function initialStaffChoice(member: PlatformMember | undefined): {
  mode: AccessMode;
  ids: string[];
} {
  const current = member?.locationAccess;
  if (current && !current.allLocations && current.locationIds.length > 0) {
    return { mode: 'specific', ids: current.locationIds };
  }
  return { mode: 'all', ids: [] };
}

function toAccessBody(mode: AccessMode, ids: string[]): SetPlatformMemberLocationAccessBody {
  return mode === 'all' ? { allLocations: true } : { allLocations: false, locationIds: ids };
}

/**
 * PUT the location access, then read back what the SERVER stored (DNKADM-0304 U3): the PUT
 * returns no body we rely on, and a server-side coercion must render rather than drift until
 * reload. The save has already succeeded once the PUT resolves, so a FAILED re-read must not
 * present as a failed save - it falls back to the submitted state.
 */
async function saveLocationAccess(
  userId: number | string,
  body: SetPlatformMemberLocationAccessBody,
): Promise<PlatformMemberLocationAccess> {
  await setLocationAccess(userId, body);
  let saved: PlatformMemberLocationAccess = body.allLocations
    ? { allLocations: true, locationIds: [] }
    : { allLocations: false, locationIds: body.locationIds };
  try {
    const reread = await getLocationAccess(userId);
    saved = { allLocations: reread.allLocations, locationIds: reread.locationIds };
  } catch {
    // Fall back to the submitted state.
  }
  return saved;
}

/** DNKADM-0453: the outcome of "change the role, then save the location access". */
interface RoleWithAccessResult {
  /** The row after the writes: the role response, with the saved access when the PUT succeeded. */
  row: PlatformMember;
  /** The classified PUT failure when the role changed but the locations did not save, else null. */
  accessError: string | null;
}

/**
 * Change the role, THEN (when `access` is given) save the location access. A role failure
 * throws to the caller (nothing changed). If the role succeeds and the PUT fails, the result
 * carries the role's row and the plain error - the caller says the role changed and the
 * locations did not save.
 */
async function changeRoleThenAccess(
  userId: number,
  role: AssignableRole,
  access: SetPlatformMemberLocationAccessBody | undefined,
): Promise<RoleWithAccessResult> {
  const updated = await setPlatformMemberRole(userId, role);
  if (!access) return { row: updated, accessError: null };
  try {
    const saved = await saveLocationAccess(userId, access);
    return { row: { ...updated, locationAccess: saved }, accessError: null };
  } catch (err) {
    return { row: updated, accessError: classifyLocationAccessError(err) };
  }
}

/** The Invited tab's pending-access line: "All locations" or the names; null renders nothing. */
function pendingAccessLabel(
  access: PlatformMemberLocationAccess | null | undefined,
  locationNames: Map<string, string>,
): string | null {
  if (!access) return null;
  if (access.allLocations) return 'All locations';
  if (access.locationIds.length === 0) return null;
  return access.locationIds.map((id) => locationNames.get(id) ?? id).join(', ');
}

/**
 * DNKADM-0453: the required "Location access" choice shared by the Add platform user and the
 * change-to-Staff dialogs - All locations (default) or Specific locations, using the same
 * multi-select as the Edit location access dialog. Pure presentation; the dialog owns the state.
 */
function LocationAccessChoice({
  mode,
  onModeChange,
  selectedIds,
  onSelectedChange,
  locations,
  disabled,
  invalid,
}: {
  mode: AccessMode;
  onModeChange: (mode: AccessMode) => void;
  selectedIds: string[];
  onSelectedChange: (ids: string[]) => void;
  locations: V2Location[];
  disabled: boolean;
  invalid: boolean;
}) {
  const ids = useId();
  const legendId = `${ids}-legend`;
  const selectLabelId = `${ids}-select-label`;
  return (
    <Stack spacing={1.5}>
      <FormControl component="fieldset" disabled={disabled}>
        <FormLabel component="legend" id={legendId} required>
          Location access
        </FormLabel>
        <RadioGroup
          value={mode}
          onChange={(e) => onModeChange(e.target.value as AccessMode)}
          name={`${ids}-location-access-mode`}
          aria-labelledby={legendId}
        >
          <FormControlLabel value="all" control={<Radio size="small" />} label="All locations" />
          <FormControlLabel
            value="specific"
            control={<Radio size="small" />}
            label="Specific locations"
          />
        </RadioGroup>
      </FormControl>
      {mode === 'specific' ? (
        <FormControl
          size="small"
          fullWidth
          disabled={disabled || locations.length === 0}
          error={invalid}
        >
          <InputLabel id={selectLabelId}>Choose locations</InputLabel>
          <Select
            labelId={selectLabelId}
            multiple
            value={selectedIds}
            onChange={(e) =>
              onSelectedChange(
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
              )
            }
            input={<OutlinedInput label="Choose locations" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const loc = locations.find((l) => l.id === id);
                  return <Chip key={id} size="small" label={loc ? loc.name || loc.id : id} />;
                })}
              </Box>
            )}
          >
            {locations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                <Checkbox checked={selectedIds.includes(loc.id)} size="small" />
                <ListItemText primary={loc.name || loc.id} />
              </MenuItem>
            ))}
          </Select>
          {locations.length === 0 ? (
            <FormHelperText>No locations are available to assign.</FormHelperText>
          ) : null}
        </FormControl>
      ) : null}
    </Stack>
  );
}

/**
 * DNKADM-0453: "change to Staff" with the location choice in the same step. On submit the role
 * changes, then the location access is saved. A role failure keeps the dialog open with the
 * error; a role success with a failed PUT closes it and the screen says the role changed and the
 * locations did not save (the row stays editable). The choice starts at All, or at the login's
 * current specific locations when it has them (so an Admin with a list is not widened by default).
 */
function StaffRoleDialog({
  member,
  locations,
  onClose,
  onDone,
}: {
  member: PlatformMember;
  locations: V2Location[];
  onClose: () => void;
  onDone: (target: PlatformMember, result: RoleWithAccessResult) => void;
}) {
  const ids = useId();
  const titleId = `${ids}-title`;
  const seed = initialStaffChoice(member);
  const [accessMode, setAccessMode] = useState<AccessMode>(seed.mode);
  const [accessIds, setAccessIds] = useState<string[]>(seed.ids);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  async function handleSubmit() {
    setInlineError(null);
    if (accessMode === 'specific' && accessIds.length === 0) {
      setInlineError(PICK_LOCATION_MESSAGE);
      return;
    }
    setSubmitting(true);
    try {
      onDone(
        member,
        await changeRoleThenAccess(member.userId, 'STAFF', toAccessBody(accessMode, accessIds)),
      );
    } catch (err) {
      setInlineError(toPlatformMemberWriteErrorView(err).message);
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={titleId}>Change to Staff</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 600 }}>
              {member.name || member.email || member.userId}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {member.email || PLACEHOLDER}
            </Typography>
          </Box>
          <LocationAccessChoice
            mode={accessMode}
            onModeChange={setAccessMode}
            selectedIds={accessIds}
            onSelectedChange={setAccessIds}
            locations={locations}
            disabled={submitting}
            invalid={inlineError === PICK_LOCATION_MESSAGE}
          />
          {inlineError ? (
            <Alert severity="error" role="alert">
              {inlineError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? 'Saving…' : 'Change to Staff'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function seedAccess(
  access: PlatformMemberLocationAccess,
  setMode: (mode: AccessMode) => void,
  setSelectedLocationIds: (ids: string[]) => void,
): void {
  setMode(access.allLocations ? 'all' : 'specific');
  setSelectedLocationIds(access.locationIds);
}

function classifyLocationAccessError(error: unknown): string {
  const code = v2ErrorCode(error);
  if (error instanceof ApiError && error.code === 'FORBIDDEN') {
    return locationScopedErrorMessage(error) ?? "You don't have access to this location.";
  }
  if (error instanceof ApiError && error.code === 'NOT_FOUND') {
    return 'That platform user could not be found.';
  }
  if (error instanceof ApiError && error.code === 'VALIDATION') {
    if (matchesCode(code, ['EMPTY', 'REQUIRED'])) {
      return 'Pick at least one location, or choose All locations.';
    }
    if (matchesCode(code, ['OWNER'])) {
      return 'Owners always have access to all locations.';
    }
    if (matchesCode(code, ['LOCATION', 'FOREIGN', 'UNKNOWN', 'NOT_FOUND'])) {
      return 'One or more selected locations is not available to assign.';
    }
    const msg = serverMessage(error);
    if (msg) {
      const lower = msg.toLowerCase();
      if (lower.includes('owner')) return 'Owners always have access to all locations.';
      if (lower.includes('empty') || lower.includes('least one') || lower.includes('required')) {
        return 'Pick at least one location, or choose All locations.';
      }
      if (lower.includes('location')) {
        return 'One or more selected locations is not available to assign.';
      }
      return msg;
    }
    return 'That location access could not be saved.';
  }
  return serverMessage(error) ?? 'That location access could not be saved.';
}

function matchesCode(code: string | undefined, needles: string[]): boolean {
  if (!code) return false;
  const upper = code.toUpperCase();
  return needles.some((needle) => upper.includes(needle));
}

/**
 * The roster's fail-closed line (DNKADM-0015): a failed read means the roster is
 * UNKNOWN, never "0 members".
 */
const PLATFORM_ROSTER_UNAVAILABLE =
  'We couldn’t reach the Platform Users roster, so it isn’t shown. ' +
  'This is not an empty roster — please try again.';
